import { GoogleMap, Marker, StandaloneSearchBox, useJsApiLoader } from '@react-google-maps/api'
import React, { CSSProperties, useCallback, useEffect, useRef } from 'react'

interface FormData {
  activityName: string
  destination: {
    name: string
    address: string
    lat: number
    long: number
    country: string
  } | null
  startTime: { hour: string; minute: string; period: string }
  additionalTimes: Array<{ hour: string; minute: string; period: string }>
  transportModes: string[]
  tourType: string
  maxPeople: string
  [key: string]: any
}

interface StepProps {
  onNext: () => void
  onPrevious: () => void
  onStepProgress: (progress: number) => void
  formData: FormData
  updateFormData: (data: Partial<FormData>) => void
}

const libraries = ['places', 'geometry'] as any[]

const Step2: React.FC<StepProps> = ({
  onNext,
  onPrevious,
  onStepProgress,
  formData,
  updateFormData,
}) => {
  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null)

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    libraries,
  })

  const calculateProgress = useCallback(() => {
    let completedFields = 0
    if (formData.activityName?.trim()) completedFields += 25
    if (formData.destination) completedFields += 25
    if (formData.startTime?.hour && formData.startTime?.minute) completedFields += 25
    if (formData.tourType) completedFields += 25
    return Math.min(completedFields, 100)
  }, [formData.activityName, formData.destination, formData.startTime, formData.tourType])

  useEffect(() => {
    onStepProgress(calculateProgress())
  }, [formData, onStepProgress, calculateProgress])

  const handleInputChange = (field: string, value: any) => {
    updateFormData({ [field]: value })
  }

  const handleTransportToggle = (mode: string) => {
    const currentModes = formData.transportModes || []
    const newModes = currentModes.includes(mode)
      ? currentModes.filter((m) => m !== mode)
      : [...currentModes, mode]
    updateFormData({ transportModes: newModes })
  }

  const addStartingTime = () => {
    const currentTimes = formData.additionalTimes || []
    updateFormData({
      additionalTimes: [...currentTimes, { hour: '', minute: '', period: 'AM' }],
    })
  }

  const handlePlaceSelect = () => {
    const places = searchBoxRef.current?.getPlaces()
    const place = places?.[0]

    if (place?.geometry?.location) {
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      const address = place.formatted_address || ''
      const name = place.name || ''
      const countryComponent = place.address_components?.find((c) => c.types.includes('country'))
      const country = countryComponent?.long_name || ''

      // This is the corrected section
      updateFormData({
        destination: {
          name,
          address,
          lat,
          long: lng,
          country,
        },
      })
    }
  }

  const canProceed =
    formData.activityName && formData.destination && formData.startTime?.hour && formData.tourType

  const styles = {
    container: {
      backgroundColor: '#fff',
      padding: '16px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      width: '100%',
      maxWidth: '500px',
      margin: '0 auto',
      boxSizing: 'border-box',
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
    label: {
      display: 'block',
      fontSize: '16px',
      color: '#212427',
      marginBottom: '8px',
      fontWeight: '500',
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #D6DAE0',
      borderRadius: '8px',
      fontSize: '16px',
      boxSizing: 'border-box',
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
    buttonPrimary: {
      padding: '12px 24px',
      backgroundColor: '#0048B3',
      color: 'white',
      border: 'none',
      borderRadius: '24px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '16px',
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
    buttonSecondary: {
      padding: '12px 24px',
      backgroundColor: 'transparent',
      color: '#667085',
      border: '1px solid #D6DAE0',
      borderRadius: '24px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontFamily: 'Poppins, sans-serif',
    } as CSSProperties,
  }

  if (loadError) return <div>Error loading maps.</div>
  if (!isLoaded || !window.google?.maps) return <div>Loading Maps...</div>

  return (
    <div style={styles.container}>
      {/* Activity Name */}
      <div style={{ marginBottom: '24px' }}>
        <label style={styles.label}>
          Name of the activity <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <input
          type="text"
          value={formData.activityName || ''}
          onChange={(e) => handleInputChange('activityName', e.target.value)}
          style={styles.input}
        />
      </div>

      {/* Destination - Now with Google Maps Autocomplete */}
      <div style={{ marginBottom: '24px' }}>
        <label style={styles.label}>
          Destination <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <StandaloneSearchBox
          onLoad={(ref) => {
            searchBoxRef.current = ref
            if (window.google?.maps) {
              const bounds = new window.google.maps.LatLngBounds(
                { lat: 5.6, lng: 97.3 },
                { lat: 45.6, lng: 146.0 },
              )
              ref.setBounds(bounds)
            }
          }}
          onPlacesChanged={handlePlaceSelect}
        >
          <input
            type="text"
            placeholder="Search for a destination..."
            value={formData.destination?.name || ''}
            onChange={(e) => {
              // This is the corrected section
              if (e.target.value === '') {
                updateFormData({ destination: null })
              } else {
                updateFormData({
                  destination: {
                    ...formData.destination,
                    name: e.target.value,
                    address: formData.destination?.address || '',
                    lat: formData.destination?.lat || 0,
                    long: formData.destination?.long || 0,
                    country: formData.destination?.country || '',
                  },
                })
              }
            }}
            style={styles.input}
          />
        </StandaloneSearchBox>

        {formData.destination && (
          <div
            style={{
              position: 'relative',
              height: '300px',
              width: '100%',
              marginTop: '12px',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={{ lat: formData.destination.lat, lng: formData.destination.long }}
              zoom={12}
              options={{
                fullscreenControl: false,
                zoomControl: false,
                streetViewControl: false,
                mapTypeControl: false,
                draggable: false,
              }}
            >
              <Marker
                position={{ lat: formData.destination.lat, lng: formData.destination.long }}
              />
            </GoogleMap>
          </div>
        )}
      </div>

      {/* Available Starting Time */}
      <div style={{ marginBottom: '24px' }}>
        <label style={styles.label}>
          Available starting time <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
          <input
            type="text"
            placeholder="hour"
            value={formData.startTime?.hour || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '')
              if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12)) {
                handleInputChange('startTime', { ...(formData.startTime || {}), hour: value })
              }
            }}
            style={{ ...styles.input, width: '80px', textAlign: 'center' }}
          />
          <span style={{ color: '#667085' }}>:</span>
          <input
            type="text"
            placeholder="min"
            value={formData.startTime?.minute || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '')
              if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 59)) {
                handleInputChange('startTime', { ...(formData.startTime || {}), minute: value })
              }
            }}
            style={{ ...styles.input, width: '80px', textAlign: 'center' }}
          />
          <div
            style={{
              display: 'flex',
              backgroundColor: '#F3F4F6',
              borderRadius: '8px',
              padding: '4px',
            }}
          >
            <button
              onClick={() =>
                handleInputChange('startTime', { ...(formData.startTime || {}), period: 'AM' })
              }
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: formData.startTime?.period === 'AM' ? '#0048B3' : 'transparent',
                color: formData.startTime?.period === 'AM' ? 'white' : '#667085',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              AM
            </button>
            <button
              onClick={() =>
                handleInputChange('startTime', { ...(formData.startTime || {}), period: 'PM' })
              }
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                backgroundColor: formData.startTime?.period === 'PM' ? '#0048B3' : 'transparent',
                color: formData.startTime?.period === 'PM' ? 'white' : '#667085',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              PM
            </button>
          </div>
        </div>

        {/* Additional Starting Times */}
        {(formData.additionalTimes || []).map((time, index) => (
          <div
            key={index}
            style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}
          >
            <input
              type="text"
              placeholder="hour"
              value={time.hour}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '')
                if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 12)) {
                  const updatedTimes = [...(formData.additionalTimes || [])]
                  updatedTimes[index] = { ...updatedTimes[index], hour: value }
                  handleInputChange('additionalTimes', updatedTimes)
                }
              }}
              style={{ ...styles.input, width: '80px', textAlign: 'center' }}
            />
            <span style={{ color: '#667085' }}>:</span>
            <input
              type="text"
              placeholder="minute"
              value={time.minute}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '')
                if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 59)) {
                  const updatedTimes = [...(formData.additionalTimes || [])]
                  updatedTimes[index] = { ...updatedTimes[index], minute: value }
                  handleInputChange('additionalTimes', updatedTimes)
                }
              }}
              style={{ ...styles.input, width: '80px', textAlign: 'center' }}
            />
            <div
              style={{
                display: 'flex',
                backgroundColor: '#F3F4F6',
                borderRadius: '8px',
                padding: '4px',
              }}
            >
              <button
                onClick={() => {
                  const updatedTimes = [...(formData.additionalTimes || [])]
                  updatedTimes[index] = { ...updatedTimes[index], period: 'AM' }
                  handleInputChange('additionalTimes', updatedTimes)
                }}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: time.period === 'AM' ? '#0048B3' : 'transparent',
                  color: time.period === 'AM' ? 'white' : '#667085',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                AM
              </button>
              <button
                onClick={() => {
                  const updatedTimes = [...(formData.additionalTimes || [])]
                  updatedTimes[index] = { ...updatedTimes[index], period: 'PM' }
                  handleInputChange('additionalTimes', updatedTimes)
                }}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: time.period === 'PM' ? '#0048B3' : 'transparent',
                  color: time.period === 'PM' ? 'white' : '#667085',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                PM
              </button>
            </div>
            <button
              onClick={() => {
                const updatedTimes = (formData.additionalTimes || []).filter((_, i) => i !== index)
                handleInputChange('additionalTimes', updatedTimes)
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#ef4444',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
              }}
            >
              &times;
            </button>
          </div>
        ))}
        <button
          onClick={addStartingTime}
          style={{
            background: 'none',
            border: 'none',
            color: '#0048B3',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontWeight: '500',
          }}
        >
          + Add another starting time
        </button>
      </div>

      {/* Transport Mode */}
      <div style={{ marginBottom: '24px' }}>
        <label style={styles.label}>
          Transport mode <span style={{ color: '#667085' }}>(optional)</span>
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {['Walking', 'Private transport', 'Public transport'].map((mode) => (
            <label
              key={mode}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#212427',
              }}
            >
              <input
                type="checkbox"
                checked={(formData.transportModes || []).includes(mode)}
                onChange={() => handleTransportToggle(mode)}
                style={{ width: '18px', height: '18px', accentColor: '#0048B3' }}
              />
              {mode}
            </label>
          ))}
        </div>
      </div>

      {/* Tour Type */}
      <div style={{ marginBottom: '24px' }}>
        <label style={styles.label}>
          Tour type <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <div style={{ display: 'flex', gap: '24px' }}>
          {[
            { id: 'group', label: 'Group tour' },
            { id: 'private', label: 'Private tour' },
          ].map((type) => (
            <label
              key={type.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#212427',
              }}
            >
              <input
                type="radio"
                name="tourType"
                value={type.id}
                checked={formData.tourType === type.id}
                onChange={(e) => handleInputChange('tourType', e.target.value)}
                style={{ width: '18px', height: '18px', accentColor: '#0048B3' }}
              />
              {type.label}
            </label>
          ))}
        </div>
      </div>

      {/* Max People */}
      <div style={{ marginBottom: '32px' }}>
        <label style={styles.label}>
          No. of maximum people <span style={{ color: '#667085' }}>(optional)</span>
        </label>
        <input
          type="number"
          placeholder="Enter max no. of people allowed"
          value={formData.maxPeople || ''}
          onChange={(e) => handleInputChange('maxPeople', e.target.value)}
          style={styles.input}
        />
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={onPrevious} style={styles.buttonSecondary}>
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          style={{
            ...styles.buttonPrimary,
            backgroundColor: canProceed ? '#0048B3' : '#D6DAE0',
            cursor: canProceed ? 'pointer' : 'not-allowed',
          }}
        >
          Next
        </button>
      </div>
    </div>
  )
}

export default Step2
